import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';

import { AppIcons } from '../../../../core/icons/fa-icons';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { TableColumn, TableAction } from '../../../shared/models/table-models';
import { Setup } from '../../shared/service/setup';
import { Table } from '../../../shared/table/table';
import { BackButton } from '../../../shared/buttons/back-button';

@Component({
  selector: 'app-tehsil',
  standalone: true,
  imports: [TranslatePipe, CommonModule, Table, Popup, BackButton],
  templateUrl: './tehsil.html',
  styleUrl: './tehsil.css',
})
export class Tehsil implements OnInit {

  icons = AppIcons;

  citySeq!: number;

  list: any[] = [];
  columns: TableColumn[] = [];

  showPopup = false;
  isEditMode = false;
  isViewMode = false;
  formData: any = {};

  fields: DynamicFieldConfig[] = [];

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private setup: Setup,
    private toastr: ToastrService,
    private picklist: PickListService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.citySeq = Number(params.get('id'));
      this.loadPicklists();
    });
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.view,
      tooltip: 'COMMON.VIEW',
      class: 'action-view',
      onClick: (row) => this.openPopup(row, true),
    },
    {
      icon: this.icons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
    },
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.thslNm });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.delete(this.selectedRow);
    this.selectedRow = null;
  }

  // ================= INIT =================
  loadPicklists() {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$
      .pipe(take(1))
      .subscribe(() => {
        this.buildFields();
        this.buildColumns();
        this.loadData();
      });
  }

  // ================= FORM =================
  buildFields() {
    this.fields = [
      {
        key: 'thslSeq',
        label: 'SETUP.ID',
        type: 'text',
        hidden: true,
      },
      {
        key: 'thslNm',
        label: 'COMMON.NAME',
        type: 'text',
        validations: { required: true },
      },
  
      {
        key: 'thslStsKey',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: { required: true },
      },
          {
        key: 'thslDesc',
        label: 'COMMON.DESCRIPTION',
        type: 'textarea',
      },
    ];
  }

  // ================= TABLE =================
  buildColumns() {
    this.columns = [
      { key: 'thslSeq', label: 'SETUP.ID' },
      { key: 'thslNm', label: 'COMMON.NAME', type: 'link' },
      { key: 'thslDesc', label: 'COMMON.DESCRIPTION' },
      {
        key: 'thslStsKey',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: {
          706: 'COMMON.ACTIVE',
          707: 'COMMON.INACTIVE'
        },
        badgeMap: {
          706: 'status-active',
          707: 'status-inactive'
        }
      }
    ];
  }

  // ================= LOAD DATA =================
  loadData() {
    this.setup.getTehsils(this.citySeq).subscribe({
      next: (res: any) => {

        const data = Array.isArray(res)
          ? res
          : (res?.data || res?.result || []);

        this.list = data.map((t: any) => ({
          lctnSeq: t.lctnSeq,
          thslSeq: t.thslSeq,
          thslCd: t.thslCd,
          thslNm: t.thslNm,
          thslDesc: t.thslDesc,
          thslStsKey: t.thslStsKey
        }));
      },
      error: () => {
        this.list = [];
        this.toastr.error(this.translate.instant('SETUP.GEOGRAPHY.LOAD_TEHSILS_FAILED'));
      }
    });
  }

  // ================= POPUP =================
  openPopup(data?: any, viewMode: boolean = false) {
    this.showPopup = true;
    this.isViewMode = viewMode;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        lctnSeq: data.lctnSeq,
        thslSeq: data.thslSeq,
        thslNm: data.thslNm,
        thslDesc: data.thslDesc,
        thslStsKey: data.thslStsKey
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        lctnSeq: null,
        thslSeq: null,
        thslNm: '',
        thslDesc: '',
        thslStsKey: this.getDefaultStatus()
      };
    }
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || 706;
  }

  // ================= SAVE =================
handleSave = (form: any) => {

  const payload = {
    thslSeq: this.isEditMode ? form.thslSeq : null,   // ✅ FIX HERE
    thslCd: '',
    thslDesc: form.thslDesc,
    thslNm: form.thslNm,
    dstrctSeq: this.citySeq,
    thslStsKey: form.thslStsKey,
    delFlg: 0,
    tntKey: 0
  };

  return this.setup.saveTehsil(payload).subscribe({
    next: () => {
      this.toastr.success(this.translate.instant(this.isEditMode ? 'SETUP.GEOGRAPHY.TEHSIL_UPDATED' : 'SETUP.GEOGRAPHY.TEHSIL_CREATED'));
      this.showPopup = false;
      this.loadData();
    },
    error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED'))
  });
};

  // ================= DELETE =================
  delete(row: any) {

    const payload = {
      thslSeq: row.thslSeq,
      thslCd: row.thslCd || '',
      thslDesc: row.thslDesc || '',
      thslNm: row.thslNm,
      dstrctSeq: this.citySeq,
      thslStsKey: row.thslStsKey,
      delFlg: 1,
      tntKey: 0
    };

    this.setup.saveTehsil(payload).subscribe({
      next: (res: any) => {
        if (res?.error) {
          this.toastr.error(res.error);
          return;
        }

        this.toastr.success(this.translate.instant('SETUP.DELETED_SUCCESSFULLY'));
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.DELETE_FAILED'))
    });
  }
}